import React, { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useFormContext, useWatch } from 'react-hook-form'
import { useCurrencyExchangeRate } from 'shared/currency-exchange-rate/api/fetchCurrencyExchangeRate'
import notifyExchangeRateError from 'shared/currency-exchange-rate/components/notifyExchangeRateError'
import { ConsignmentItemFormValues } from 'features/declaration/consignment-item/models/schema'
import { useCalculatorModal } from 'features/declaration/form/components/calculator/CalculatorModalContext'
import FormCollapsibleColumn from '../../../../../../components/p5/FormCollapsibleColumn'
import FormField from '../../../../../../components/p5/FormField'
import Input from '../../../../../../components/p5/Input'
import useDeclarationFieldRequirements
  from '../../../components/useDeclarationFieldRequirements'
import CommodityCodeSearch from '../../../../consignment-item/components/CommodityCodeSearch'
import { useDeclarationEditorContext } from '../../../contexts/DeclarationEditorContext'

function EnsGoodsData() {
  const { t } = useTranslation()

  const { editorData } = useDeclarationEditorContext()

  const {
    setValue, control,
  } = useFormContext<ConsignmentItemFormValues>()

  const departureCustomsOffice = editorData?.departureCustomsOffice
  const country = useMemo(
    () => departureCustomsOffice?.slice('DEPARTURE_OFFICE_'.length, 'DEPARTURE_OFFICE_'.length + 2) ?? null,
    [departureCustomsOffice],
  )

  const [initialPriceCurrency, initialPrice] = useWatch({
    control,
    name: ['initialPriceCurrency', 'initialPrice'],
  })

  const [inputCurrencyCountry, setInputCurrencyCountry] = useState<string | null>(initialPriceCurrency)
  const { data: exchangeRate, error: exchangeRateError } = useCurrencyExchangeRate(inputCurrencyCountry)
  const { open } = useCalculatorModal()
  useEffect(() => {
    let price = initialPrice
    if (price !== null && exchangeRate && exchangeRate > 0) {
      price = Number.parseFloat((price * (1 / exchangeRate)).toFixed(3))
    }
    setValue('price', price)
  }, [initialPrice, exchangeRate, setValue])

  useEffect(() => {
    if (exchangeRateError) {
      notifyExchangeRateError(exchangeRateError)
    }
  }, [exchangeRateError])

  useEffect(() => {
    switch (initialPriceCurrency) {
      case 'EUR':
        setInputCurrencyCountry(null)
        break
      case 'USD':
        setInputCurrencyCountry('US')
        break
      case 'GBP':
        setInputCurrencyCountry('GB')
        break
      case 'RON':
        setInputCurrencyCountry('RO')
        break
      default:
        setInputCurrencyCountry(null)
    }
  }, [initialPriceCurrency])

  const fieldRequirements = useDeclarationFieldRequirements<ConsignmentItemFormValues>('consignmentItem', [
    'commodityDescriptionOfGoods',
    'commodityCode',
    'commodityHarmonizedSystemSubHeadingCode',
    'countryOfDestination',
    'countryOfDispatch',
    'price',
    'referenceNumberUCR',
    'dutyAmount',
    'dutyRate',
    'vatRate',
    'excise',
  ])

  return (
    <section className="py-3">
      <FormCollapsibleColumn
        columnId="declaration-goods-data"
        headingLabel={t('declaration.p5.goodsData')}
      >
        <FormField
          labelKey="declaration.p5.commodityDescriptionOfGoods"
          fieldRule={fieldRequirements.commodityDescriptionOfGoods}
        >
          <Input
            <ConsignmentItemFormValues>
            maxLength={250}
            type="textarea"
            field="commodityDescriptionOfGoods"
            labelKey="declaration.p5.commodityDescriptionOfGoods"
            placeholder={t('declaration.p5.commodityDescriptionOfGoods')}
            fieldRule={fieldRequirements.commodityDescriptionOfGoods}
          />
        </FormField>
        <FormField
          scope="commodityHarmonizedSystemSubHeadingCode"
          labelKey="declaration.p5.commodityCode"
          fieldRule={fieldRequirements.commodityHarmonizedSystemSubHeadingCode}
        >
          <CommodityCodeSearch country={country} isEns />
        </FormField>
        <FormField
          labelKey="declaration.p5.goodsMeasureGrossMass"
          fieldRule={fieldRequirements.goodsMeasureGrossMass}
        >
          <div className="input-group">
            <Input
              <ConsignmentItemFormValues>
              field="goodsMeasureGrossMass"
              labelKey="declaration.p5.goodsMeasureGrossMass"
              type="number"
              inputMode="decimal"
              min={0}
              step={0.001}
              placeholder={t('declaration.p5.goodsMeasureGrossMass')}
              fieldRule={fieldRequirements.goodsMeasureGrossMass}
            />
            <div className="input-group-append">
              <button
                type="button"
                className="btn btn-light h-100 border fw-bold"
                onClick={() => {
                  open({
                    onResolve: (value: number) => {
                      setValue('goodsMeasureGrossMass', value)
                    },
                  })
                }}
              >
                <i className="fas fa-calculator" />
              </button>
            </div>
          </div>
        </FormField>
      </FormCollapsibleColumn>
    </section>
  )
}

export default EnsGoodsData
